import React, { useMemo } from 'react';
import styled from 'styled-components';
import { DataTable } from '../../components/DataTable';
import { NegoceFilterBar } from './NegoceFilterBar';
import { useNegoceFilters } from './NegoceFilters';
import { useRawData } from 'apiTyped/hooks/negoce.query';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatNumber = (n: number | undefined | null): string =>
  n != null ? n.toLocaleString('fr-FR') : '—';

// ─── Component ──────────────────────────────────────────────────────────────

export const NegoceRawData: React.FC = () => {
  const { campaignId, wineId, saleTypeId, negociantId } = useNegoceFilters();

  const { isLoadingRawData, rawData } = useRawData({
    campaignId,
    saleTypeId,
    wineId,
    negociantId
  });

  const rows = (rawData as any)?.rows || [];

  const columns = useMemo(
    () => [
      {
        key: 'negociant_name',
        header: 'Négociant',
        sortable: true,
        width: '16%',
        render: (row: any) => <strong>{row.negociant_name}</strong>
      },
      {
        key: 'customer_name',
        header: 'Customer',
        sortable: true,
        width: '16%',
        render: (row: any) => <span>{row.customer_name}</span>
      },
      {
        key: 'country_name',
        header: 'Country',
        width: '12%',
        render: (row: any) => <span>{row.country_name}</span>
      },
      {
        key: 'wine_name',
        header: 'Wine',
        width: '16%',
        render: (row: any) => <span>{row.wine_name}</span>
      },
      {
        key: 'sale_type',
        header: 'Sale type',
        width: '12%',
        render: (row: any) => <span>{row.sale_type_name || row.sale_type}</span>
      },
      {
        key: 'bottles_sold',
        header: 'Bottles (eq. 75cl)',
        sortable: true,
        width: '14%',
        render: (row: any) => <Mono>{formatNumber(row.bottles_sold)}</Mono>
      },
      {
        key: 'price',
        header: 'Price',
        sortable: true,
        width: '10%',
        render: (row: any) => (
          <Mono>{row.price != null ? `${row.price.toFixed(2)} €` : '—'}</Mono>
        )
      }
    ],
    []
  );

  return (
    <Page>
      <Header>
        <Title>Raw Data</Title>
        <Subtitle>
          Complete detailed data for your négoce campaign. Use filters to narrow
          results.
        </Subtitle>
        {rows.length > 0 && <RowCount>{rows.length} rows</RowCount>}
      </Header>

      <NegoceFilterBar showNegociant />

      <DataTable
        columns={columns}
        data={rows}
        unavailable={!campaignId && !isLoadingRawData}
        emptyMessage={
          isLoadingRawData ? 'Loading...' : 'No data for this selection'
        }
      />
    </Page>
  );
};

// ─── Styled ─────────────────────────────────────────────────────────────────

const Page = styled.div`
  max-width: 1400px;
`;

const Header = styled.header`
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-family: var(--font-serif);
  font-size: 28px;
  font-weight: 500;
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: var(--ink-muted);
  margin: 0;
  max-width: 640px;
  line-height: 1.5;
`;

const RowCount = styled.div`
  margin-top: 8px;
  font-size: 12px;
  font-weight: 500;
  color: var(--terracotta);
`;

const Mono = styled.span`
  font-variant-numeric: tabular-nums;
`;
